import {
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useCurrency } from "@/contexts/CurrencyContext";
import { Undo2 } from "lucide-react";

export function PriceRangeSlider({
    form,
    product,
    shop,
    optimizationSettings,
    setOptimizationSettings,
    selectedMarket,
    recalculateFormValues,
    productPrice,
}: {
    form: any;
    product: any;
    shop: any;
    optimizationSettings: any;
    setOptimizationSettings: any;
    selectedMarket: any;
    recalculateFormValues: any;
    productPrice: number;
}) {
    const { currencySymbol, currencyCode } = useCurrency();

    const defaultMinPrice =
        product.price * (1 + 0.01 * shop?.settings?.optimizations.min_price);
    const defaultMaxPrice =
        product.price * (1 + 0.01 * shop?.settings?.optimizations.max_price);

    let isOverridden = false;
    if (selectedMarket === "all") {
        if (
            defaultMinPrice !== optimizationSettings?.base_market?.min_price ||
            defaultMaxPrice !== optimizationSettings?.base_market?.max_price
        ) {
            isOverridden = true;
        }
    } else {
        if (
            optimizationSettings?.markets?.[selectedMarket]?.min_price !==
                undefined ||
            optimizationSettings?.markets?.[selectedMarket]?.max_price !==
                undefined
        ) {
            isOverridden = true;
        }
    }

    // Allow slider to go as low as 5% of the base market price and as high as 200% of the base market price.
    // Need to either pass in the converted price or convert it here.
    // I think pass it in since the conversion is done in the parent.
    const sliderMin = 0.05 * productPrice;
    const sliderMax = 2 * productPrice;

    const updatePriceRange = (minPrice: number, maxPrice: number) => {
        if (selectedMarket === "all") {
            setOptimizationSettings({
                ...optimizationSettings,
                base_market: {
                    ...optimizationSettings.base_market,
                    min_price: minPrice,
                    max_price: maxPrice,
                },
            });
        } else {
            setOptimizationSettings({
                ...optimizationSettings,
                markets: {
                    ...optimizationSettings.markets,
                    [selectedMarket]: {
                        ...optimizationSettings.markets[selectedMarket],
                        min_price: minPrice,
                        max_price: maxPrice,
                    },
                },
            });
        }
    };

    const handleBaseReset = () => {
        if (selectedMarket === "all") {
            setOptimizationSettings({
                ...optimizationSettings,
                base_market: {
                    ...optimizationSettings.base_market,
                    min_price: defaultMinPrice,
                    max_price: defaultMaxPrice,
                },
            });
        } else {
            setOptimizationSettings({
                ...optimizationSettings,
                markets: {
                    ...optimizationSettings.markets,
                    [selectedMarket]: {
                        ...optimizationSettings.markets[selectedMarket],
                        min_price: undefined,
                        max_price: undefined,
                    },
                },
            });
        }

        recalculateFormValues();
    };

    return (
        <FormField
            control={form.control}
            name="minPrice"
            render={({ field }) => (
                <FormItem>
                    <FormLabel className="flex items-center gap-2">
                        Price range ({currencySymbol}
                        {field.value.toFixed(2)} - {currencySymbol}
                        {form.watch("maxPrice").toFixed(2)})<span>•</span>
                        <span className="text-sm text-muted-foreground">
                            Current price:
                            <span className="font-semibold text-muted-foreground rounded-md bg-muted p-1 py-0.5 ml-1">
                                {currencySymbol}
                                {productPrice.toFixed(2)}
                            </span>
                        </span>
                        •
                        <span className="text-sm text-muted-foreground">
                            Margin:{" "}
                            <span className="font-semibold text-muted-foreground rounded-md bg-muted p-1 py-0.5 ml-1">
                                {(
                                    ((form.watch("minPrice") -
                                        form.watch("cost")) /
                                        form.watch("minPrice")) *
                                    100
                                ).toFixed(1)}
                                %
                            </span>
                            {" - "}
                            <span className="font-semibold text-muted-foreground rounded-md bg-muted p-1 py-0.5">
                                {(
                                    ((form.watch("maxPrice") -
                                        form.watch("cost")) /
                                        form.watch("maxPrice")) *
                                    100
                                ).toFixed(1)}
                                %
                            </span>
                        </span>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="ml-auto"
                            onClick={(e) => {
                                e.preventDefault();
                                handleBaseReset();
                            }}
                            disabled={!isOverridden}
                        >
                            <Undo2 className="h-3.5 w-3.5" />
                            Reset
                        </Button>
                    </FormLabel>
                    <FormDescription className="pb-3">
                        What are the absolute minimum and maximum prices you're
                        willing to sell this product for?
                    </FormDescription>
                    <Slider
                        min={sliderMin}
                        max={sliderMax}
                        step={0.1}
                        value={[form.watch("minPrice"), form.watch("maxPrice")]}
                        onValueChange={(values) => {
                            form.setValue("minPrice", values[0]);
                            form.setValue("maxPrice", values[1]);
                            updatePriceRange(values[0], values[1]);
                        }}
                    />
                </FormItem>
            )}
        />
    );
}
